/**
 * Frame-accurate `m:ss:ff` timecode parsing and formatting for user-typed
 * timing fields.
 *
 * Distinct from `timeFormat.ts`, which formats for *display*: `timeFormat`
 * zero-pads minutes, carries hours, and floors frames, while these round to the
 * nearest frame so a value survives a format → parse → format round trip.
 * `convertMMSSToSeconds` there is not a substitute — it does no validation and
 * returns `NaN` on bad input, which a field the user types into cannot use.
 *
 * Extracted from `CompositionQuickEditPopover` (PLAT-10951) so the transcript
 * quick-edit and the clip-comment timecode field share one parser.
 */

/** Fallback frame rate when a surface doesn't supply one. */
export const DEFAULT_FRAMERATE = 30;

/**
 * PLAT-10509 — timecode frame math must use a WHOLE frame rate. Source fps is
 * often fractional (23.976, 29.97); a fractional `fps` in `frames = totalFrames
 * % fps` yields a fractional frame field (e.g. `23.047952…`). Rounding to whole
 * frames makes timecodes read `m:ss:ff` and makes the up/down steppers move
 * full frames only.
 */
export function wholeFps(framerate: number): number {
  return Math.max(1, Math.round(framerate > 0 ? framerate : DEFAULT_FRAMERATE));
}

/**
 * Format seconds as a frame-accurate `m:ss:ff` timecode (minutes : seconds :
 * frames) at the given frame rate. Frames are the natural editing resolution
 * for overlay timing, so the last field counts frames (0…fps-1) rather than
 * hundredths.
 */
export function formatTimecode(
  totalSeconds: number,
  framerate: number = DEFAULT_FRAMERATE,
): string {
  const fps = wholeFps(framerate);
  const totalFrames = Math.max(0, Math.round(totalSeconds * fps));
  const frames = totalFrames % fps;
  const totalSec = Math.floor(totalFrames / fps);
  const sec = totalSec % 60;
  const m = Math.floor(totalSec / 60);
  return `${m}:${String(sec).padStart(2, '0')}:${String(frames).padStart(2, '0')}`;
}

/**
 * Parse a timing input. Accepts `m:ss:ff` (frames), `m:ss`, or a plain seconds
 * value (`12.34`). Frames are validated against `framerate`. Returns null when
 * the value is empty or malformed.
 */
export function parseTimecode(
  input: string,
  framerate: number = DEFAULT_FRAMERATE,
): number | null {
  const fps = wholeFps(framerate);
  const t = input.trim();
  if (t === '') return null;
  if (t.includes(':')) {
    const parts = t.split(':');
    if (parts.length === 2) {
      const m = Number(parts[0]);
      const sec = Number(parts[1]);
      if (!Number.isFinite(m) || !Number.isFinite(sec)) return null;
      if (m < 0 || sec < 0 || sec >= 60) return null;
      return m * 60 + sec;
    }
    if (parts.length === 3) {
      const m = Number(parts[0]);
      const sec = Number(parts[1]);
      const fr = Number(parts[2]);
      if (![m, sec, fr].every(Number.isFinite)) return null;
      if (m < 0 || sec < 0 || sec >= 60 || fr < 0 || fr >= fps) return null;
      return m * 60 + sec + fr / fps;
    }
    return null;
  }
  const v = Number(t);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

/** Round a time to the nearest whole frame — the resolution edits commit at. */
export function snapToFrame(seconds: number, framerate: number): number {
  const fps = wholeFps(framerate);
  return Math.round(seconds * fps) / fps;
}
